import { useId, useState, type ReactNode } from 'react';
import type { Transfer } from '../lib/types';
import {
  compactSignedSats,
  formatShortTimestamp,
  formatSignedSats,
  formatTimestamp,
} from '../lib/format';
import { computeMagnitudes } from '../lib/magnitude';
import { dayLabel } from '../lib/dayLabel';
import { CopyButton } from './CopyButton';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
} from './icons';
import styles from './TransferList.module.css';

const BLOCKSTREAM_TX_BASE = 'https://blockstream.info/tx/';

const timeOfDay = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});
function exactUtcTimestamp(iso: string | null): string {
  if (!iso) return 'Pending';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Pending';
  return `${date.toISOString().replace('T', ' ').slice(0, 16)} UTC`;
}

function transactionShapeNote(transfer: Transfer): string {
  const { vinCount, voutCount } = transfer;
  if (vinCount <= 1 && voutCount <= 1) {
    return 'A single-input, single-output transaction.';
  }
  return `One of ${vinCount} input${vinCount === 1 ? '' : 's'} and ${voutCount} output${voutCount === 1 ? '' : 's'} on the underlying transaction — this is only this address's side of it.`;
}

function TransferAmount({ deltaSat }: { deltaSat: number }) {
  const [revealed, setRevealed] = useState(false);
  const amount = compactSignedSats(deltaSat);

  if (!amount.truncated) {
    return <span className={styles.amount}>{amount.full}</span>;
  }

  return (
    <button
      type="button"
      className={`${styles.amount} ${styles.amountToggle}`}
      aria-pressed={revealed}
      aria-label={
        revealed
          ? `${amount.full}. Show shortened amount`
          : `${amount.compact}. Show full amount`
      }
      title={revealed ? 'Show shortened amount' : 'Show full amount'}
      onClick={() => setRevealed((r) => !r)}
    >
      {revealed ? amount.full : amount.compact}
    </button>
  );
}

/** Plain-English size note for a notably large transfer. */
function magnitudeNote(percentile: number): string {
  return percentile >= 100
    ? 'Largest of the loaded transfers'
    : `Top ${Math.max(1, 100 - percentile)}% of loaded transfers by size`;
}

/**
 * `compactAmounts` shows each amount's first 6 significant digits, with the
 * amount itself a button that reveals the full value (the rest of the row
 * still expands the transaction details). Off by default; the dashboard
 * turns it on to cut clutter.
 */
export function TransferList({
  transfers,
  compactAmounts = false,
  groupByDay = false,
  limit,
  emptyState,
}: {
  transfers: Transfer[];
  compactAmounts?: boolean;
  /**
   * Show only the first `limit` transfers. Sizes are still ranked against
   * the whole list, so "large for this address" means the same everywhere.
   */
  limit?: number;
  /** Insert a day heading (Today, Yesterday, …) before each day's transfers. */
  groupByDay?: boolean;
  /** Replaces the default "No transfers found." message. */
  emptyState?: ReactNode;
}) {
  const [expandedTxid, setExpandedTxid] = useState<string | null>(null);
  const groupIdPrefix = useId();

  if (transfers.length === 0) {
    return emptyState ?? <p className={styles.empty}>No transfers found.</p>;
  }

  const magnitudes = computeMagnitudes(transfers);
  const magnitudeByTxid = new Map(transfers.map((t, i) => [t.txid, magnitudes[i]]));
  const shown = limit === undefined ? transfers : transfers.slice(0, limit);

  const renderRow = (transfer: Transfer) => {
    const magnitude = magnitudeByTxid.get(transfer.txid)!;
    const isExpanded = expandedTxid === transfer.txid;
    const detailId = `transfer-detail-${transfer.txid}`;
    const when =
      transfer.confirmationState === 'pending'
        ? 'Pending'
        : groupByDay && transfer.timestamp
          ? timeOfDay.format(new Date(transfer.timestamp))
          : formatShortTimestamp(transfer.timestamp);

    return (
      <li key={transfer.txid} className={styles.row}>
        <div className={styles.summary}>
          <span
            className={`${styles.directionIcon} ${
              transfer.direction === 'in' ? styles.in : styles.out
            }`}
            aria-hidden="true"
          >
            {transfer.direction === 'in' ? (
              <ArrowDownIcon width={14} height={14} />
            ) : (
              <ArrowUpIcon width={14} height={14} />
            )}
          </span>
          <span className={styles.amountBlock}>
            {compactAmounts ? (
              <TransferAmount deltaSat={transfer.deltaSat} />
            ) : (
              <span className={styles.amount}>{formatSignedSats(transfer.deltaSat)}</span>
            )}
            {magnitude.isNotable && (
              <span className={styles.magnitudeNote}>
                {magnitudeNote(magnitude.percentile)}
              </span>
            )}
          </span>
          {/* Its ::after stretches over the whole row, so clicking anywhere
              but the amount toggle still expands the details. */}
          <button
            type="button"
            className={styles.meta}
            aria-expanded={isExpanded}
            aria-controls={detailId}
            aria-label={`${formatSignedSats(transfer.deltaSat)}, ${
              transfer.confirmationState === 'pending'
                ? 'pending'
                : formatTimestamp(transfer.timestamp)
            }. ${isExpanded ? 'Hide' : 'Show'} transaction details`}
            onClick={() => setExpandedTxid(isExpanded ? null : transfer.txid)}
          >
            <span
              className={`${styles.confirmation} ${
                transfer.confirmationState === 'pending' ? styles.pending : ''
              }`}
            >
              {when}
            </span>
            <span className={styles.chevron} aria-hidden="true">
              {isExpanded ? (
                <ChevronDownIcon width={11} height={11} />
              ) : (
                <ChevronRightIcon width={11} height={11} />
              )}
            </span>
          </button>
        </div>

        {isExpanded && (
          <div id={detailId} className={styles.detail}>
            <div className={styles.detailRow}>
              <span>Transaction ID</span>
              <span className={styles.detailTxid}>{transfer.txid}</span>
            </div>
            <div className={styles.detailRow}>
              <span>Time</span>
              <span>{exactUtcTimestamp(transfer.timestamp)}</span>
            </div>
            <div className={styles.detailRow}>
              <span>Block</span>
              <span>{transfer.blockHeight ?? 'Not yet confirmed'}</span>
            </div>
            <p className={styles.detailNote}>{transactionShapeNote(transfer)}</p>
            <div className={styles.detailActions}>
              <a
                href={`${BLOCKSTREAM_TX_BASE}${transfer.txid}`}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.txLink}
              >
                View on Blockstream
                <ExternalLinkIcon width={12} height={12} />
              </a>
              <CopyButton value={transfer.txid} label="Copy txid" />
            </div>
          </div>
        )}
      </li>
    );
  };

  if (!groupByDay) {
    return <ul className={styles.list}>{shown.map(renderRow)}</ul>;
  }

  // Grouped view: bucket the rows ourselves rather than trusting the API's
  // order — pending first, then each local day newest first, then any
  // confirmed transfer whose time isn't known.
  const groups = new Map<string, { sortKey: number; rows: Transfer[] }>();
  const add = (label: string, sortKey: number, transfer: Transfer) => {
    const group = groups.get(label) ?? { sortKey, rows: [] };
    group.sortKey = Math.max(group.sortKey, sortKey);
    group.rows.push(transfer);
    groups.set(label, group);
  };
  for (const transfer of shown) {
    const time = transfer.timestamp ? Date.parse(transfer.timestamp) : NaN;
    if (transfer.confirmationState === 'pending')
      add('Not yet confirmed', Infinity, transfer);
    else if (Number.isNaN(time)) add('Time unknown', -Infinity, transfer);
    else add(dayLabel(transfer.timestamp), time, transfer);
  }
  const ordered = [...groups.entries()].sort((x, y) => y[1].sortKey - x[1].sortKey);

  return (
    <div className={styles.groups}>
      {ordered.map(([label, group]) => {
        const headingId = `${groupIdPrefix}-${label.replace(/\W+/g, '-').toLowerCase()}`;
        const rows = [...group.rows].sort(
          (x, y) =>
            (Date.parse(y.timestamp ?? '') || 0) - (Date.parse(x.timestamp ?? '') || 0),
        );
        return (
          <section key={label} aria-labelledby={headingId} className={styles.group}>
            <h2 id={headingId} className={styles.dayHeading}>
              {label}
            </h2>
            <ul className={styles.list}>{rows.map(renderRow)}</ul>
          </section>
        );
      })}
    </div>
  );
}
